import React from 'react';
import type { ClarityAnalysis as ClarityAnalysisType, ClarityCheckItem } from '@/types/analysis';

interface ClarityAnalysisProps {
  clarity: ClarityAnalysisType;
}

const ClarityCheckItemComponent: React.FC<{ check: ClarityCheckItem }> = ({ check }) => {
  const getStatusStyles = (status: string) => {
    switch (status) {
      case 'Pass':
        return {
          border: 'border-green-200',
          bg: 'bg-green-50',
          badge: 'bg-green-100 text-green-800',
          icon: 'check_circle',
          iconColor: 'text-green-500'
        };
      case 'Warn':
        return {
          border: 'border-yellow-200',
          bg: 'bg-yellow-50',
          badge: 'bg-yellow-100 text-yellow-800',
          icon: 'warning',
          iconColor: 'text-yellow-500'
        };
      case 'Fail':
        return {
          border: 'border-red-200',
          bg: 'bg-red-50',
          badge: 'bg-red-100 text-red-800',
          icon: 'cancel',
          iconColor: 'text-red-500'
        };
      default:
        return {
          border: 'border-gray-200',
          bg: 'bg-gray-50',
          badge: 'bg-gray-100 text-gray-800',
          icon: 'info',
          iconColor: 'text-gray-500'
        };
    }
  };

  const styles = getStatusStyles(check.status);
  const displayName = check.check_name === 'Idiom Usage' ? 'Ambiguous Language' : check.check_name;

  return (
    <div className={`border rounded-lg p-4 ${styles.border} ${styles.bg}`}>
      <div className="flex items-center justify-between mb-2">
        <h5 className="font-medium text-gray-900">{displayName}</h5>
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${styles.badge}`}>
          {check.status}
        </span>
      </div>
      <div className="flex items-start gap-2">
        <span className={`material-symbols-outlined text-lg ${styles.iconColor}`}>
          {styles.icon}
        </span>
        <div className="flex-1">
          {check.reason && (
            <p className="text-sm text-gray-700 mb-2">{check.reason}</p>
          )}
          {check.details && Array.isArray(check.details) && check.details.length > 0 && (
            <div className="space-y-2">
              {check.details.map((detail: any, idx: number) => (
                <div key={idx} className="text-sm">
                  {detail.quote && (
                    <blockquote className="text-gray-600 italic border-l-2 border-gray-300 pl-3 mb-1">
                      "{detail.quote}"
                    </blockquote>
                  )}
                  {detail.reason && (
                    <p className="text-gray-700 mb-1">{detail.reason}</p>
                  )}
                  {detail.improvement && (
                    <p className="text-green-700 text-xs">💡 {detail.improvement}</p>
                  )}
                </div>
              ))}
            </div>
          )}
          {check.quote && (
            <blockquote className="text-sm text-gray-600 italic border-l-2 border-gray-300 pl-3 mb-2">
              "{check.quote}"
            </blockquote>
          )}
          {check.suggestions && check.suggestions.length > 0 && (
            <div className="mt-2">
              <p className="text-xs font-medium text-gray-700 mb-1">Suggestions:</p>
              <ul className="text-xs text-gray-600 list-disc list-inside space-y-1">
                {check.suggestions.map((suggestion, idx) => (
                  <li key={idx}>{suggestion}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export const ClarityAnalysis: React.FC<ClarityAnalysisProps> = ({ clarity }) => {
  const score = clarity.calculated_clarity_score;
  const percentage = (score / 10) * 100;

  const getScoreLabel = (score: number) => {
    if (score >= 8) return 'Excellent clarity';
    if (score >= 6) return 'Good clarity';
    if (score >= 4) return 'Moderate clarity';
    return 'Needs improvement';
  };

  const getScoreColor = (score: number) => {
    if (score >= 8) return 'bg-green-500';
    if (score >= 6) return 'bg-blue-500';
    if (score >= 4) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  return (
    <div>
      {/* Overall Clarity Score */}
      <div className="mb-8">
        <div className="flex items-end space-x-2 mb-2">
          <span className="text-5xl font-extrabold text-gray-900">
            {score.toFixed(1)}
          </span>
          <span className="text-2xl font-semibold text-gray-400">/10</span>
        </div>
        <p className="text-sm text-gray-500 mb-4">
          {getScoreLabel(score)}
        </p>
        <div className="w-full bg-gray-200 rounded-full h-2.5">
          <div
            className={`h-2.5 rounded-full ${getScoreColor(score)} transition-all duration-500`}
            style={{ width: `${percentage}%` }}
          />
        </div>
      </div>

      {/* Clarity Checklist */}
      <div className="space-y-4 mb-8">
        {clarity.clarity_checklist.map((check, index) => (
          <ClarityCheckItemComponent key={index} check={check} />
        ))}
      </div>

      {/* Overall Assessment */}
      {clarity.overall_assessment && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h5 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
            <span className="material-symbols-outlined text-blue-500">
              assessment
            </span>
            Overall Assessment
          </h5>
          <p className="text-sm text-gray-700">{clarity.overall_assessment}</p>
        </div>
      )}

      {/* Improvement Suggestions */}
      {clarity.improvement_suggestions && clarity.improvement_suggestions.length > 0 && (
        <div className="mt-4 bg-primary-50 border border-primary-200 rounded-lg p-4">
          <h5 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
            <span className="material-symbols-outlined text-primary-500">
              lightbulb
            </span>
            Improvement Suggestions
          </h5>
          <ul className="text-sm text-gray-700 space-y-2">
            {clarity.improvement_suggestions.map((suggestion, idx) => (
              <li key={idx} className="flex items-start gap-2">
                <span className="material-symbols-outlined text-primary-500 text-base mt-0.5">
                  arrow_forward
                </span>
                <span>{suggestion}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

